import { mobileNumberSchema, OTP_LENGTH, otpCodeSchema } from "@anthra/platform/domain"
import { ApiError, ERROR_CODES, secondsUntil } from "@anthra/platform/errors"
import { isSafeDestination, peekDestination } from "@anthra/platform/session"
import { SwamithraLogo, WhatsAppIcon } from "@anthra/ui/brand-icons"
import { Button } from "@anthra/ui/button"
import { cn } from "@anthra/ui/cn"
import { ErrorState } from "@anthra/ui/feedback"
import { Field } from "@anthra/ui/field"
import { LightThemeIcon, ThemeIcon } from "@anthra/ui/icons"
import { PhoneNumberInput, SIGN_IN_COPY } from "@anthra/ui/phone-number-input"
import { SignInSplit } from "@anthra/ui/sign-in-hero"
import { Skeleton, SkeletonText } from "@anthra/ui/skeleton"
import { SectionTitle } from "@anthra/ui/typography"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router"
import { type ReactNode, useEffect, useState } from "react"

import { copy } from "@/copy/expert-login"
import {
	usePanelSession,
	useRequestPanelOtp,
	useVerifyPanelOtp,
} from "@/features/auth/use-panel-auth"
import { useTheme } from "@/hooks/use-theme"
import { panelEntryPhase, usePanelEntryStore } from "@/stores/use-entry-store"
import { useSessionStore } from "@/stores/use-session-store"

/**
 * The panel login screen (US-01-005.1/.2/.7/.9/.10/.13/.24).
 *
 * Built against screens/expert-login.html: the masthead, the editorial aside and
 * the two-step card, in the design's own two-column layout.
 *
 * THERE IS NO SIGN-UP AND NO NUMBER LOOKUP HERE, and both absences are the
 * requirement: Experts are provisioned by a Super Admin (US-01-005.13), and an
 * unknown number, a deactivated Expert and a mistyped digit are all answered with
 * the same generic 'not authorised' so the screen cannot be used to enumerate who
 * is on the panel (US-01-005.6/.7).
 *
 * On success the Expert lands on the screen the deep link asked for rather than
 * the dashboard (US-01-005.24).
 */
/** A clock with the hand sweeping on; used beside the code's expiry. */
function ClockIcon() {
	return (
		<Glyph>
			<path d="M20.5 12a8.5 8.5 0 1 1-2.6-6.1" />
			<path d="M20.8 3.6v4.2h-4.2" />
			<path d="M12 7.4V12l3.1 1.9" />
		</Glyph>
	)
}

/** The shared frame for the small glyphs on this screen. */
function Glyph({ children }: { children: ReactNode }) {
	return (
		<svg
			className="size-5"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			{children}
		</svg>
	)
}

export const Route = createFileRoute("/login")({
	component: PanelLogin,
})

/** The sign-in photograph, served from this app's public folder. */
const SIGN_IN_HERO_SRC = `${import.meta.env.BASE_URL}images/sign-in-hero.webp`

function PanelLogin() {
	const navigate = useNavigate()
	const router = useRouter()
	const phase = usePanelEntryStore(panelEntryPhase)
	const consumeDestination = useSessionStore(state => state.consumeDestination)
	const sessionStatus = useSessionStore(state => state.status)
	// The passive /expert/me probe. An Expert who is already signed in and
	// lands on /login (by typing it in the address bar, following a stale
	// bookmark, or browser-back after sign-in) must be sent on to the
	// dashboard — the store's `status` is only flipped by the OTP verify
	// mutation in this tab, so a live cookie alone would otherwise leave them
	// staring at the login card.
	const session = usePanelSession()
	const [theme, toggleTheme] = useTheme()
	const themeLabel = theme === "dark" ? "Switch to light theme" : "Switch to dark theme"
	const ThemeActionIcon = theme === "dark" ? LightThemeIcon : ThemeIcon

	// A held destination — an Expert who followed a WhatsApp deep link and was
	// bounced here to verify first (US-01-001.22, US-01-004.15, US-01-005.24) —
	// is read once up front with a PEEK, not consumed: `CodeStep` only names what
	// is held so the person knows where they are headed; `useVerifyPanelOtp` is
	// what actually takes and spends it once verification succeeds.
	const heldDestination = useQuery({
		queryKey: ["panel-login", "held-destination"],
		queryFn: () => peekDestination(),
	})

	// The login flow's state lives exactly as long as this screen does — see the
	// comment on the same pattern in the user app's routes/entry.tsx. Clearing it
	// on unmount, rather than from the verify mutation's onSuccess, means it
	// cannot unmount <CodeStep> before the mutate()-level onSuccess that navigates
	// has run.
	useEffect(() => () => usePanelEntryStore.getState().reset(), [])

	// TWO PATHS OFF THE LOGIN SCREEN, ONE EFFECT — mirrors the user app's
	// entry.tsx (TC-US-00-001-1-1 step 4).
	//
	// (a) OTP verify succeeds in this tab: `useVerifyPanelOtp`'s hook-level
	// `onSuccess` flips `useSessionStore.status` to "authenticated". The
	// hook-level callback runs unconditionally, unlike a mutate()-level one
	// tied to a component that may already be gone.
	//
	// (b) A live cookie is already in place — the `/expert/me` probe returned
	// an identity. The store field never gets set on this path, because
	// nothing in this tab verified an OTP; the query cache is the only signal.
	useEffect(() => {
		const isAuthed = sessionStatus === "authenticated" || !!session.data
		if (!isAuthed) return
		const destination = consumeDestination()
		if (destination !== null && isSafeDestination(destination)) {
			// THROUGH THE ROUTER, NOT THE HISTORY. The destination held for it
			// is basepath-RELATIVE (`router.state.location.href`). When the app
			// was served under "/panel", `history.push` resolved that against the
			// origin and an Expert who followed a WhatsApp deep link landed on a
			// path this app did not serve (TC-US-01-005-7-5). It now has its own
			// domain and no basepath, but the router stays the one that applies it.
			//
			// `router.navigate` applies the basepath, which makes the round trip
			// through holdDestination symmetrical: relative in, relative out.
			void router.navigate({ to: destination, replace: true })
			return
		}
		void navigate({ to: "/", replace: true })
	}, [sessionStatus, session.data, consumeDestination, router, navigate])

	return (
		<SignInSplit
			heroSrc={SIGN_IN_HERO_SRC}
			header={
				/* The logo centred, the theme toggle on the right. */
				<header
					data-public-header
					className="border-border dark:bg-surface w-full shrink-0 self-stretch border-b bg-[#fcf2e8]"
				>
					{/* THE LOGO IN THE MIDDLE. Three columns: an empty one, the logo,
					    and the theme toggle; the outer two share what is left
					    equally, so the logo sits at the true centre of the bar. */}
					<div className="mx-auto grid h-[5.5rem] w-full max-w-[1700px] grid-cols-[1fr_auto_1fr] items-center gap-3 px-6 md:h-[6.5rem] lg:h-[7.5rem] lg:px-10 min-[96rem]:px-20">
						<span aria-hidden="true" />
						<SwamithraLogo
							src={`${import.meta.env.BASE_URL}images/swamithra-logo.png`}
							darkSrc={`${import.meta.env.BASE_URL}images/swamithra-logo-dark.png`}
							label="Expert Portal"
							className="h-20 md:h-24 lg:h-28"
						/>
						<Button
							type="button"
							variant="quiet"
							size="icon"
							className="justify-self-end"
							onClick={toggleTheme}
							aria-label={themeLabel}
						>
							{/* An icon, not the word "Theme" in a 44px square. */}
							<ThemeActionIcon />
						</Button>
					</div>
				</header>
			}
		>
			{/* `LoginColdLoad` covers three transient states in this order:
			    (1) the destination peek is still resolving, (2) the passive
			    /expert/me probe has not answered yet — flashing the sign-in
			    card for an Expert who is already authenticated is the bug
			    this route now fixes, and (3) the probe has answered with a
			    session and the effect above is about to navigate off this
			    screen. In all three cases the "Checking whether you are still
			    signed in…" copy is honest and prevents the card flash. */}
			{heldDestination.isPending || session.isPending || !!session.data ? (
				<LoginColdLoad />
			) : (
				/* ONE CENTRED COLUMN. The lead and the fact list that used to sit
				 * beside the card were replaced by the photograph, which now
				 * fills the left half of a wide screen (SignInSplit), for both
				 * the number and the code step. What is left is the card,
				 * centred in the right half, and on a phone the whole screen.
				 */
				<div className="mx-auto flex w-full max-w-[36rem] flex-1 flex-col justify-center gap-6 px-[var(--space-gutter-mobile)] py-8 md:px-[var(--space-gutter-tablet)] lg:max-w-none lg:px-0 lg:py-0">
					<main className="flex flex-col gap-3">
						{sessionStatus === "signed_out" ? <SignedOutNotice /> : null}

						{phase === "number" ? (
							<NumberStep />
						) : (
							<CodeStep destination={heldDestination.data ?? null} />
						)}
					</main>
				</div>
			)}
		</SignInSplit>
	)
}

/**
 * "Checking whether you are still signed in…" (design's Loading state, cold
 * load) — the brief gap while the held-destination peek above resolves, shaped
 * like the card that is about to replace it rather than a bare spinner.
 */
function LoginColdLoad() {
	return (
		<div className="mx-auto flex w-full max-w-[36rem] flex-1 flex-col justify-center gap-8 px-[var(--space-gutter-mobile)] py-8 md:px-[var(--space-gutter-tablet)] lg:px-[var(--space-gutter-desktop)] lg:py-12">
			<main className="flex flex-col gap-4">
				<div
					className="flex flex-col gap-4"
					role="status"
					aria-live="polite"
					aria-busy="true"
				>
					<span className="text-meta text-muted">
						Checking whether you are still signed in…
					</span>
					<div
						className="rounded-card border-border bg-card shadow-card flex flex-col gap-4 border p-4 lg:p-6"
						aria-hidden="true"
					>
						<div className="flex flex-col gap-2">
							<SkeletonText lines={3} />
						</div>
						<div className="flex flex-col gap-2">
							<SkeletonText lines={3} />
						</div>
						<Skeleton className="h-4 w-full" />
						<hr className="border-border border-t" />
						<div className="flex flex-col gap-2">
							<Skeleton className="h-4 w-full" />
							<Skeleton className="h-4 w-full" />
						</div>
					</div>
				</div>
			</main>
		</div>
	)
}

/**
 * "You were signed out" (design's Error state, "Returned here…" variant) —
 * shown once, on the number step, when this screen was reached because a
 * session ended rather than by choice (US-01-005.3/.17, US-00-006.10/.21).
 */
function SignedOutNotice() {
	return (
		<div
			className="rounded-card border-status-open-border bg-status-open-bg flex items-start gap-3 border p-3"
			role="status"
		>
			<div>
				<strong>You were signed out</strong>
				<p>
					Your panel session ended, so we brought you back here. Sign in again with your
					number and we will take you back to where you were.
				</p>
			</div>
		</div>
	)
}

/** The one line that protects somebody, at the foot of both cards. */
function StaffCodeNotice() {
	return (
		<p className="text-muted mt-1 text-center text-xs">
			Swamithra staff will never ask you to read an OTP aloud.
		</p>
	)
}

function NumberStep() {
	const mobileNumber = usePanelEntryStore(state => state.mobileNumber)
	const setMobileNumber = usePanelEntryStore(state => state.setMobileNumber)
	const requestOtp = useRequestPanelOtp()
	const [error, setError] = useState<string | undefined>(undefined)

	function submit(): void {
		const parsed = mobileNumberSchema.safeParse(mobileNumber)
		if (!parsed.success) {
			setError(parsed.error.issues[0]?.message)
			return
		}
		setError(undefined)
		requestOtp.mutate({ mobileNumber: parsed.data, isResend: false })
	}

	// The one distinguishable outcome (US-01-005.6/.7): `not_authorised` covers
	// BOTH an unknown number and a deactivated one, on purpose, so this screen
	// still cannot be used to learn which numbers are on the panel — it only
	// swaps the generic banner for the design's own blank-slate, same message.
	const notAuthorised =
		requestOtp.error instanceof ApiError && requestOtp.error.code === ERROR_CODES.notAuthorised

	return (
		<form
			className="animate-rise-in rise-in-stagger border-border bg-card flex flex-col gap-5 rounded-[1rem] border p-8 shadow-[0_4px_24px_rgba(0,0,0,0.03)] sm:p-10 lg:flex-1 xl:p-12"
			noValidate
			onSubmit={event => {
				event.preventDefault()
				submit()
			}}
		>
			{/* Stacked, and the step is an OVERLINE. `size-5` is a 20x20 box —
			    an icon's class on a paragraph — so "Step 1 of 2" wrapped to one
			    character per line beside the heading. */}
			{/* THE SAME WORDS AS THE USER APP'S CARD, from the same constant.
			    It said "Step 1 of 2" over "Enter your registered number" over
			    "We will send a six-digit OTP to this number on WhatsApp",
			    against the user app's "Sign in" over "We send a 6-digit OTP to
			    your WhatsApp" — the same card, in two voices, with a step
			    counter on one of them and not on the other. The counter goes:
			    both flows are two steps and neither needs to say so. */}
			<div className="flex flex-col gap-1">
				<SectionTitle
					as="h2"
					className="text-[1.125rem] leading-snug font-bold tracking-tight text-balance sm:text-[1.25rem] xl:text-[1.375rem] min-[96rem]:text-[1.5rem]"
				>
					{SIGN_IN_COPY.title}
				</SectionTitle>
				{notAuthorised ? null : (
					<p className="text-description text-subtle-foreground">
						{SIGN_IN_COPY.description}
					</p>
				)}
			</div>

			{notAuthorised ? (
				<div className="flex flex-col items-center gap-3 py-10 text-center">
					<span
						className="bg-subtle text-primary rounded-circle flex size-14 items-center justify-center"
						aria-hidden="true"
					>
						<svg
							viewBox="0 0 24 24"
							width="28"
							height="28"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
						>
							<circle
								cx="12"
								cy="12"
								r="9"
							/>
							<path d="M12 8v5" />
							<path d="M12 16h.01" />
						</svg>
					</span>
					<h4 className="text-card-title text-foreground">{copy.empty.blankSlate2}</h4>
					<p className="text-description text-subtle-foreground">{copy.empty.label}</p>
				</div>
			) : null}

			<Field
				label={SIGN_IN_COPY.numberLabel}
				hint={SIGN_IN_COPY.numberHint}
				error={error}
			>
				{({ id, describedBy, invalid }) => (
					<PhoneNumberInput
						id={id}
						value={mobileNumber}
						onChange={setMobileNumber}
						describedBy={describedBy}
						invalid={invalid}
						className="rounded-[0.75rem] shadow-sm"
					/>
				)}
			</Field>

			{/* The server's message, unchanged, for anything other than
			    `not_authorised` — branching further would reintroduce the
			    enumeration hole the API closes. */}
			{requestOtp.isError && !notAuthorised ? <ErrorState error={requestOtp.error} /> : null}

			{/* IDENTICAL TO THE USER APP'S. It was `<Button className={cn(
			    buttonVariants(...))}`, applying the variants a second time on top of
			    the ones Button already applies, and its pending state was three empty
			    <i> elements whose dot animation lived in expert-login.css — deleted
			    in this rebuild, so it rendered as a blank gap. */}
			<div className="flex flex-col gap-2">
				<Button
					type="submit"
					className="h-auto w-full rounded-[0.75rem] py-3.5 text-sm font-semibold shadow"
					disabled={requestOtp.isPending}
				>
					<WhatsAppIcon />
					{requestOtp.isPending ? "Sending…" : "Send OTP"}
				</Button>
				{requestOtp.isPending ? (
					<div
						className="bg-subtle rounded-pill h-1 w-full overflow-hidden"
						role="progressbar"
						aria-label="Sending your OTP on WhatsApp"
					>
						<div className="bg-primary rounded-pill h-full w-1/3 animate-pulse" />
					</div>
				) : null}
			</div>
			{/* One line, and only the one that protects somebody, centred at the
			    foot of the card as the user app's links are. */}
			<StaffCodeNotice />
		</form>
	)
}

function CodeStep({ destination }: { destination: string | null }) {
	const mobileNumber = usePanelEntryStore(state => state.mobileNumber)
	const challenge = usePanelEntryStore(state => state.challenge)
	const lockout = usePanelEntryStore(state => state.lockout)
	const setMobileNumber = usePanelEntryStore(state => state.setMobileNumber)

	const verify = useVerifyPanelOtp()
	const resend = useRequestPanelOtp()

	const [code, setCode] = useState("")
	const [codeError, setCodeError] = useState<string | undefined>(undefined)
	const cooldown = useCooldown(challenge?.resendAvailableAt)

	const parsedNumber = mobileNumberSchema.safeParse(mobileNumber)
	const isLockedOut = lockout !== null
	const cells = Array.from({ length: OTP_LENGTH }, (_, position) => ({
		position,
		value: code[position] ?? "",
	}))

	function setDigit(index: number, typed: string): void {
		const value = typed.replace(/\D/g, "")
		if (value === "") {
			setCode((code.slice(0, index) + " " + code.slice(index + 1)).trimEnd())
			return
		}

		// Pasting a complete WhatsApp code into any cell fills that cell and
		// the cells after it, matching the user app's OTP field.
		const next = (code.slice(0, index) + value + code.slice(index + value.length)).slice(
			0,
			OTP_LENGTH,
		)
		setCode(next)
		const focusAt = Math.min(index + value.length, OTP_LENGTH - 1)
		document.querySelector<HTMLInputElement>(`[data-otp-cell="${focusAt}"]`)?.focus()
	}

	function submit(): void {
		const parsedCode = otpCodeSchema.safeParse(code)
		if (!parsedCode.success) {
			setCodeError(parsedCode.error.issues[0]?.message)
			return
		}
		if (!parsedNumber.success) return
		setCodeError(undefined)

		// Landing on the destination — or on "/" — happens reactively in
		// PanelLogin once `useSessionStore`'s status flips to "authenticated"; see
		// the comment there. A mutate()-level onSuccess here would run only if
		// this component is still mounted, and it may not be by the time the
		// mutation settles.
		verify.mutate({ mobileNumber: parsedNumber.data, code: parsedCode.data })
	}

	return (
		<>
			{destination !== null ? (
				<div className="animate-rise-in rounded-control border-border bg-subtle text-description text-subtle-foreground flex flex-wrap items-center gap-2 border px-3 py-2">
					<div>
						<b>We are holding where you were going.</b> Sign in and we will take you
						there instead of the dashboard.
					</div>
				</div>
			) : null}

			<form
				className="animate-rise-in rise-in-stagger border-border bg-card flex flex-col gap-5 rounded-[1rem] border p-8 shadow-[0_4px_24px_rgba(0,0,0,0.03)] sm:p-10 lg:flex-1 xl:p-12"
				noValidate
				onSubmit={event => {
					event.preventDefault()
					submit()
				}}
			>
				{/* Same words as the user app's code card, plus the one thing
				    this step knows that the other does not: which number it
				    went to. The step counter goes with its partner above. */}
				<div className="flex flex-col gap-1">
					<SectionTitle
						as="h2"
						className="text-[1.125rem] leading-snug font-bold tracking-tight text-balance sm:text-[1.25rem] xl:text-[1.375rem] min-[96rem]:text-[1.5rem]"
					>
						{SIGN_IN_COPY.codeTitle}
					</SectionTitle>
					<p className="text-description text-subtle-foreground">
						{SIGN_IN_COPY.codeDescription} Sent to <b>{mobileNumber}</b>.
					</p>
				</div>

				{isLockedOut ? (
					<div
						className="rounded-card border-status-breach-border bg-status-breach-bg flex items-start gap-3 border p-3"
						role="alert"
					>
						<div>
							<strong>Sign-in paused for this number</strong>
							<p>{lockout.message}</p>
						</div>
					</div>
				) : null}

				<fieldset className="flex flex-col gap-2 border-0 p-0">
					<legend>OTP</legend>
					<div
						className="grid grid-cols-6 items-center gap-1.5 sm:gap-2"
						data-invalid={codeError === undefined ? undefined : "true"}
					>
						{cells.map(({ position: index, value: digit }) => (
							<input
								key={`otp-cell-${index}`}
								className={cn(
									"rounded-control border-border-interactive bg-input text-foreground focus-visible:border-border-interactive focus-visible:ring-ring/40 text-otp h-11 w-full min-w-0 border text-center tabular-nums outline-none focus-visible:ring-2",
									codeError !== undefined &&
										"border-destructive ring-destructive/30 ring-2",
								)}
								data-otp-cell={index}
								data-filled={digit.trim() === "" ? undefined : true}
								type="text"
								inputMode="numeric"
								pattern="[0-9]*"
								maxLength={index === 0 ? OTP_LENGTH : 1}
								autoComplete={index === 0 ? "one-time-code" : "off"}
								aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
								aria-invalid={codeError !== undefined}
								aria-describedby={
									codeError === undefined ? undefined : "panel-otp-code-error"
								}
								disabled={isLockedOut}
								value={digit.trim()}
								onChange={event => {
									setDigit(index, event.target.value)
								}}
								onKeyDown={event => {
									if (
										event.key === "Backspace" &&
										digit.trim() === "" &&
										index > 0
									) {
										document
											.querySelector<HTMLInputElement>(
												`[data-otp-cell="${index - 1}"]`,
											)
											?.focus()
									}
								}}
							/>
						))}
					</div>
					{codeError === undefined ? (
						<div className="flex flex-row items-center gap-2 [&_svg]:size-3">
							<ClockIcon />
							<p className="text-meta text-muted">
								{challenge
									? `This OTP expires in ${Math.max(0, Math.ceil(secondsUntil(challenge.expiresAt) / 60))} minutes.`
									: "Enter the OTP we sent"}
							</p>
						</div>
					) : (
						<p
							className="text-description text-destructive font-medium"
							id="panel-otp-code-error"
							role="alert"
						>
							{codeError}
						</p>
					)}
				</fieldset>

				{/* Suppressed once locked out: `toDisplayable` renders an otp_locked
			    error as the very same lockout sentence, and US-01-005.2 asks for
			    ONE on-screen message — two identical alerts are read out twice by a
			    screen reader. */}
				{verify.isError && !isLockedOut ? <ErrorState error={verify.error} /> : null}
				{resend.isError && !isLockedOut ? <ErrorState error={resend.error} /> : null}

				{/* A CONTAINER, NOT A BUTTON. This wrapper wore the full secondary
				    button class string — border, ground, 44px height, inline-flex,
				    whitespace-nowrap — around two real buttons, so the pair sat
				    inside something painted and sized to look like a third. */}
				<div className="flex flex-col gap-2">
					<Button
						type="submit"
						variant="primary"
						className="h-auto w-full rounded-[0.75rem] py-3.5 text-sm font-semibold shadow"
						disabled={isLockedOut || verify.isPending}
					>
						{verify.isPending ? (
							<>
								{/* The three empty <i /> that stood here were dots
								    animated by expert-login.css, which this rebuild
								    deleted — so they rendered as a blank gap. The
								    comment on NumberStep above says the same fix was
								    applied there; it never reached this branch. */}
								Checking…
							</>
						) : (
							"Sign in"
						)}
					</Button>
					<Button
						variant="secondary"
						className="h-auto w-full rounded-[0.75rem] py-3.5 text-sm font-semibold"
						disabled={isLockedOut || cooldown > 0 || resend.isPending}
						onClick={() => {
							if (parsedNumber.success) {
								resend.mutate({ mobileNumber: parsedNumber.data, isResend: true })
							}
						}}
					>
						{cooldown > 0 ? (
							<>
								Resend OTP in{" "}
								<span className="text-meta text-muted">{cooldown}s</span>
							</>
						) : (
							"Resend OTP"
						)}
					</Button>
				</div>

				<p className="text-meta text-muted">
					Resending does not use up any of your three attempts.{" "}
					{/* A link, not a 44px ghost button: this sits mid-sentence inside
					    a <p>, where a button-shaped control breaks the line box. */}
					<button
						type="button"
						className="text-link hover:text-link-hover focus-visible:ring-ring rounded-control underline underline-offset-2 focus-visible:ring-2 focus-visible:outline-none"
						onClick={() => {
							setMobileNumber("")
						}}
					>
						Use a different number
					</button>
				</p>

				<hr className="border-border border-t" />

				<div className="rounded-card border-status-in-progress-border bg-status-in-progress-bg flex items-start gap-3 border p-3">
					<div>
						<strong>Do not share this OTP with anyone</strong>
						<p>
							Nobody from Swamithra, the Income Tax Department or a client will ever
							ask you for it. Anyone who does is trying to reach user documents.
						</p>
					</div>
				</div>
				{/* One line, and only the one that protects somebody, centred at the
				    foot of the card as the user app's links are. */}
				<StaffCodeNotice />
			</form>
		</>
	)
}

/**
 * Seconds until the server says a resend is allowed (US-01-005.10).
 *
 * The value counted down to is the server's `resendAvailableAt`; a purely local
 * timer would reset on a reload and let the cap be bypassed.
 */
function useCooldown(resendAvailableAt: string | undefined): number {
	const [seconds, setSeconds] = useState(() =>
		resendAvailableAt ? secondsUntil(resendAvailableAt) : 0,
	)

	useEffect(() => {
		if (!resendAvailableAt) {
			setSeconds(0)
			return
		}
		setSeconds(secondsUntil(resendAvailableAt))
		const timer = setInterval(() => {
			setSeconds(secondsUntil(resendAvailableAt))
		}, 1000)
		return () => {
			clearInterval(timer)
		}
	}, [resendAvailableAt])

	return seconds
}
